import * as React from 'react';
import { useEffect, useState } from 'react';
import { Badge } from '@capawesome/capacitor-badge';
import { AscDesc } from 'stream-chat';
import styled from 'styled-components';
import { useGetNotifications } from '../../../core/api/pushNotification';
import { MessagesItems } from '../../../core/enums';
import { getDismissEnablePushNotificationsModalTimeFromLocalStorage } from '../../../core/localStorage';
import { isWebApp, isLastActionWithinTargetHours, isPushNotificationAvailable } from '../../../lib/utils';
import { UserNotification } from '../../../models/UserNotification';
import isUnreadNotification, { isPushNotificationGranted } from '../../common/pushNotification/pushNotification';
import AutoLogoutLayout from '../common/Layout';
import { dashboardPageUrl, setupAlertPageUrl } from '../../../core/constants';
import { contentTopGapWithTabMenu } from '../../cssConstants';
import BottomModal from '../../../commonComponents/bottomModal/BottomModal';
import { ApiResultMessageModel } from '../../../models/ApiResultMessage';
import { defaultModalMessage, isBetterOffApp } from '../../../core/domain';
import { useNavigateWithAppName } from '../../common/NavigateWithAppName';
import Helmet from '../../../commonComponents/Helmet';
import useNotificationStore from '../../../zustandStore/notificationStore';
import { hasValue } from '../../../lib/lib';
import TabMenu from '../common/TabMenu';
import ChatsSubPage from '../chats/ChatsSubPage';
import NotificationSubPage from './NotificationSubPage';
import useChatStore from '../../../zustandStore/chatStore';
import useClientContextStore from '../../../zustandStore/clientContextStore';

const NotificationsCentrePage = () => {
	const navigate = useNavigateWithAppName();
	const receivedNotifications = useNotificationStore((state) => state.receivedNotifications);
	const clientDetails = useClientContextStore((state) => state.clientDetails);
	const chatClient = useChatStore((state) => state.chatClient);
	const [newNotifications, setNewNotifications] = useState<UserNotification[]>([]);
	const [selectedTab, setSelectedTab] = useState<MessagesItems>(MessagesItems.Alerts);
	const [channels, setChannels] = useState<Array<{ countUnread?: () => number }>>([]);
	const isBetterOff = isBetterOffApp();

	const userNotifications = useGetNotifications();
	const setUnreadNotificationCount = useNotificationStore((state) => state.setUnreadNotificationCount);
	const unreadChatCount = channels.filter((channel) => channel.countUnread?.() > 0).length;
	const unreadNotificationCount = newNotifications?.filter((n) => isUnreadNotification(n.status))?.length || 0;
	const [modalMessage, setModalMessage] = React.useState<ApiResultMessageModel>({
		title: '',
		description: []
	});

	const handleCloseModal = () => {
		setModalMessage(defaultModalMessage);
	};

	useEffect(() => {
		const run = async () => {
			if (!isWebApp && isPushNotificationAvailable) {
				const isGranted = await isPushNotificationGranted();

				if (
					!isGranted &&
					!isLastActionWithinTargetHours(getDismissEnablePushNotificationsModalTimeFromLocalStorage(), 24)
				) {
					navigate(setupAlertPageUrl);
				}

				if (isGranted) {
					await Badge.clear(); // This will trigger iOS Request permission dialog if not granted.
				}
			}

			// Fetch chat channels for unread count
			if (chatClient && clientDetails) {
				const filters = { members: { $in: [clientDetails.userKey] }, type: 'messaging' };
				const sort = { last_message_at: -1 as AscDesc };
				const channelList = await chatClient.queryChannels(filters, sort);
				setChannels(channelList);
			}
		};
		run();
	}, [chatClient, clientDetails]);

	useEffect(() => {
		if (userNotifications) {
			const unreadNotifications = getNewNotifications(userNotifications);
			setNewNotifications(unreadNotifications);
			setUnreadNotificationCount(unreadNotifications.length);
		}
	}, [userNotifications]);

	useEffect(() => {
		if (receivedNotifications.length > 0) {
			// should only update when user already in the page(!== null, null is the initial state before get from API)
			if (newNotifications !== null)
				setNewNotifications((prev) => {
					const updated = prev.filter(
						(n) => !receivedNotifications.some((received) => n.userNotificationId === received.userNotificationId)
					);
					return [...updated, ...receivedNotifications];
				});
		}
	}, [receivedNotifications]);

	const handleBackButtonClick = () => {
		navigate(dashboardPageUrl);
	};

	const handleTabClick = (tabItem: MessagesItems) => {
		setSelectedTab(tabItem);
	};

	const getMessagesItemsWithCounts = () => {
		return {
			Alerts: unreadNotificationCount > 0 ? `Notifications (${unreadNotificationCount})` : 'Notifications',
			Chat: unreadChatCount > 0 ? `Chat (${unreadChatCount})` : 'Chat'
		} as const;
	};

	return (
		<>
			<AutoLogoutLayout
				showFooter={false}
				headerText="Messages"
				onBackButtonClick={handleBackButtonClick}
				widerView
				scrollResetDependency={selectedTab}
			>
				<Helmet>
					<title>Messages</title>
				</Helmet>
				{!isBetterOff && (
					<TabMenu
						enumType={getMessagesItemsWithCounts()}
						selectedTabItem={
							selectedTab === MessagesItems.Chat
								? getMessagesItemsWithCounts().Chat
								: getMessagesItemsWithCounts().Alerts
						}
						onTabItemClick={(tabDisplayName: string) => {
							// Map display name back to enum value
							if (tabDisplayName.startsWith('Chat')) {
								handleTabClick(MessagesItems.Chat);
							} else {
								handleTabClick(MessagesItems.Alerts);
							}
						}}
					/>
				)}

				<TabContent $isBetterOff={isBetterOff}>
					{(isBetterOff || selectedTab === MessagesItems.Alerts) && <NotificationSubPage />}
					{!isBetterOff && selectedTab === MessagesItems.Chat && <ChatsSubPage />}
				</TabContent>
			</AutoLogoutLayout>
			<BottomModal
				title={modalMessage.title}
				body={modalMessage.description}
				type={modalMessage.errorType}
				actions={modalMessage.actions}
				open={hasValue(modalMessage.title)}
				onClose={handleCloseModal}
			/>
		</>
	);
};

export default NotificationsCentrePage;

export const getNewNotifications = (userNotifications: UserNotification[]) =>
	userNotifications.filter((n) => isUnreadNotification(n.status));

const TabContent = styled.div<{ $isBetterOff?: boolean }>`
	padding-top: ${(props) => (props.$isBetterOff ? '0' : contentTopGapWithTabMenu)};
`;
